import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import type { HomeToAirportSubmitData } from './HomeToAirportTransferForm'
import HomeToAirportTransferForm from './HomeToAirportTransferForm'

const HOME_TO_AIRPORT_FORM_ID = 'home-to-airport-transfer-form'

interface Props {
  title: string
  subtitle: string
  currentTransferView: App.Checkout.UnifiedTransferView
  onBack?: () => void
  onSubmit: (data: HomeToAirportSubmitData) => void
  onRemove?: () => void
}

function CheckoutLoyaltyHomeToAirportDetailsStep(props: Props) {
  const {
    title,
    subtitle,
    currentTransferView,
    onBack,
    onSubmit,
    onRemove,
  } = props

  const [isHomeToAirportFormValid, setIsHomeToAirportFormValid] = useState(false)
  const [isDistanceTooFar, setIsDistanceTooFar] = useState(false)

  return (
    <>
      <ModalHeader
        title={title}
        onBackButtonClick={currentTransferView.isEditing ? undefined : onBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={16}>
              <Heading variant="heading4">{subtitle}</Heading>
              <BodyText variant="medium">
                <FormattedMessage
                  id="ZPVR0k9Un96-YmgSir_2"
                  defaultMessage="Enjoy complimentary transfers within 30km of the airport, with a small fee for longer distances."
                  description="Informational text about complimentary home-to-airport transfer distance limits"
                />
              </BodyText>
              <Divider kind="primary" />
            </VerticalSpacer>

            <HomeToAirportTransferForm
              formId={HOME_TO_AIRPORT_FORM_ID}
              initialData={{
                pickUpPlaceId: currentTransferView.pickUpPlaceId,
                pickUpPlaceName: currentTransferView.pickUpPlace?.address,
                dropOffAirportCode: currentTransferView.dropOffAirportCode,
                date: currentTransferView.transferItems[0]?.transfer?.date,
                time: currentTransferView.transferItems[0]?.transfer?.time,
                flightNumber: currentTransferView.transferItems[0]?.transfer?.flightNumber,
              }}
              onSubmit={onSubmit}
              onDistanceExceedsMax={setIsDistanceTooFar}
              onFormValidChange={setIsHomeToAirportFormValid}
            />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <Group direction="horizontal" gap={8} horizontalAlign="end">
          {currentTransferView.isEditing && (
            <TextButton kind="secondary" onClick={onRemove}>
              <FormattedMessage
                id="odbYXp_bOi_JxFwuyIoQ"
                defaultMessage="Remove"
                description="Button to remove the home-to-airport transfer from the loyalty booking"
              />
            </TextButton>
          )}
          {!currentTransferView.isEditing && (
            <TextButton
              kind="primary"
              type="submit"
              form={HOME_TO_AIRPORT_FORM_ID}
              disabled={isDistanceTooFar || !isHomeToAirportFormValid}
            >
              <FormattedMessage
                id="20Bh2RkgNGNAkNaxMUKC"
                defaultMessage="Add to booking"
                description="Button to add the home-to-airport transfer to the loyalty checkout booking"
              />
            </TextButton>
          )}
          {currentTransferView.isEditing && (
            <TextButton
              kind="primary"
              type="submit"
              form={HOME_TO_AIRPORT_FORM_ID}
              disabled={isDistanceTooFar || !isHomeToAirportFormValid}
            >
              <FormattedMessage
                id="XT_i-uAXQpqYPrxHyps1"
                defaultMessage="Update"
                description="Button to save changes to an existing home-to-airport transfer in checkout"
              />
            </TextButton>
          )}
        </Group>
      </ModalFooter>
    </>
  )
}

export default CheckoutLoyaltyHomeToAirportDetailsStep
